import { useMemo } from 'react';
import { Pressable, View, type ViewProps } from 'react-native';
import { Text } from '@/components/ui/text';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { isToday, isYesterday, format, formatISO, parseISO } from 'date-fns';
import { ConsumedDateRange } from '@/lib/local-api/meal/consumed-date-range';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { useQuery } from '@apollo/client/react';
import { type Meal, MealsByIdsDocument } from '~/graphql/generated/graphql';
import { MealUtils } from '@fytness/shared';
import { Icon } from '~/global/components/icon/Icon';

const DEFAULT_CALORIE_TARGET = 2000;

interface DayGroup {
  dateKey: string;
  label: string;
  mealCount: number;
  calories: number;
  carbs: number;
  protein: number;
  fats: number;
  goalPercent: number;
}

function getGoalColorClass(percent: number): string {
  if (percent >= 100) return 'text-destructive';
  if (percent >= 80) return 'text-success';
  return 'text-accent';
}

interface MealHistoryItemProps {
  group: DayGroup;
  isToday: boolean;
  isLast: boolean;
  onPress: () => void;
}

const MealHistoryItem = ({
  group,
  isToday: today,
  isLast,
  onPress,
}: MealHistoryItemProps) => {
  const { t } = useTranslation('meal');

  return (
    <Pressable
      onPress={onPress}
      className={`flex flex-row items-center gap-3.5 p-4 active:opacity-70 ${!isLast ? 'border-b border-border' : ''}`}
    >
      <View className="flex-1 gap-0.5">
        <Text
          className={`font-inter-bold text-xs ${today ? 'text-accent' : 'text-text-secondary'}`}
          style={{ letterSpacing: 0.5 }}
        >
          {group.label}
        </Text>
        <Text className="font-inter-medium text-sm text-text-primary">
          {t('history.mealsLogged', { count: group.mealCount })}
        </Text>
        <Text className="font-inter-regular text-xs text-text-muted">
          {group.calories.toLocaleString()} cal &bull; {group.carbs}g carbs
          &bull; {group.protein}g protein &bull; {group.fats}g fat
        </Text>
      </View>
      <View className="flex flex-row items-center gap-2">
        <View className="items-end gap-0.5">
          <Text
            className={`font-sora-bold text-sm ${getGoalColorClass(group.goalPercent)}`}
          >
            {group.goalPercent}%
          </Text>
          <Text className="font-inter-medium text-[10px] text-text-muted">
            {t('history.ofGoal')}
          </Text>
        </View>
        <Icon name="ChevronRight" size={16} className="text-text-muted" />
      </View>
    </Pressable>
  );
};

export interface MealHistorySectionProps extends ViewProps {}

export const MealHistorySection = ({
  className,
  ...props
}: MealHistorySectionProps) => {
  const { t } = useTranslation('meal');
  const router = useRouter();

  const handleLogMeal = () => {
    router.push({
      pathname: '/meals/[date]',
      params: { date: formatISO(new Date(), { representation: 'date' }) },
    });
  };

  const handleDayPress = (dateKey: string) => {
    router.push({
      pathname: '/meals/[date]',
      params: { date: dateKey },
    });
  };

  const now = new Date();
  const weekStart = ConsumedDateRange.startOfWeekFor(now);
  // The SQL bounds are a deliberate superset (widened a day each side); the
  // exact local-day decision is made in memory by `filterToDays`.
  const { fromDate, toDate } = ConsumedDateRange.forWeekToDate(now);

  const { data: rawMealLogs = [] } = useLivePowerSyncQuery<{
    id: string;
    mealId: string;
    consumedAmount: number | null;
    consumedAt: string;
  }>(db.MealLog.queries.findByDateRange(fromDate, toDate), [fromDate, toDate]);

  // `fromDate`/`toDate` are the memo keys: they change only when the week window
  // does, unlike the per-render `now`/`weekStart` Date objects.
  const mealLogs = useMemo(
    () => ConsumedDateRange.filterToDays(rawMealLogs, weekStart, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rawMealLogs, fromDate, toDate],
  );

  const mealIds = useMemo(
    () => Array.from(new Set(mealLogs.map((l) => l.mealId).filter(Boolean))),
    [mealLogs],
  );

  const { data: mealsData } = useQuery(MealsByIdsDocument, {
    variables: { ids: mealIds as string[] },
    skip: mealIds.length === 0,
  });

  const mealsById = useMemo(() => {
    const map = new Map<string, Meal>();
    (mealsData?.mealsByIds ?? []).forEach((m) => map.set(m.id, m as Meal));
    return map;
  }, [mealsData]);

  const getDayLabel = (date: Date): string => {
    if (isToday(date)) return t('history.today');
    if (isYesterday(date)) return t('history.yesterday');
    return format(date, 'EEEE');
  };

  const dayGroups = useMemo((): DayGroup[] => {
    const groups = new Map<
      string,
      {
        date: Date;
        mealCount: number;
        calories: number;
        carbs: number;
        protein: number;
        fats: number;
      }
    >();

    for (const log of mealLogs) {
      if (!log.consumedAt) continue;
      const date = ConsumedDateRange.toCalendarDate(log.consumedAt);
      const key = format(date, 'yyyy-MM-dd');

      if (!groups.has(key)) {
        groups.set(key, {
          date,
          mealCount: 0,
          calories: 0,
          carbs: 0,
          protein: 0,
          fats: 0,
        });
      }

      const group = groups.get(key)!;
      group.mealCount += 1;

      const meal = mealsById.get(log.mealId);
      if (meal) {
        const adj = MealUtils.adjustedNutrition(meal, log.consumedAmount ?? 0);
        group.calories += adj.calories ?? 0;
        group.protein += adj.protein ?? 0;
        group.carbs += adj.carbs ?? 0;
        group.fats += adj.fats ?? 0;
      }
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([dateKey, group]) => ({
        dateKey,
        label: getDayLabel(group.date),
        mealCount: group.mealCount,
        calories: Math.round(group.calories),
        carbs: Math.round(group.carbs),
        protein: Math.round(group.protein),
        fats: Math.round(group.fats),
        goalPercent: Math.round(
          (group.calories / DEFAULT_CALORIE_TARGET) * 100,
        ),
      }));
  }, [mealLogs, mealsById, t]);

  const isEmpty = mealLogs.length === 0;

  return (
    <View className={className} {...props}>
      <View className="flex flex-row items-center justify-between mb-3">
        <View className="flex flex-row items-center gap-3">
          <View className="h-0.5 w-6 rounded-sm bg-accent" />
          <Text className="font-inter-bold text-xs tracking-widest text-text-secondary uppercase">
            {t('history.thisWeek')}
          </Text>
        </View>
        <Pressable
          testID="meal-log-action"
          onPress={handleLogMeal}
          className="flex flex-row items-center gap-1.5"
        >
          <Icon name="Plus" size={14} className="text-accent" />
          <Text className="font-inter-medium text-xs text-accent">
            {t('logMealAction')}
          </Text>
        </Pressable>
      </View>

      <View className="rounded-xl bg-surface overflow-hidden">
        {isEmpty ? (
          <View className="py-8">
            <Text className="font-inter-regular text-text-muted text-center text-sm">
              {t('history.empty')}
            </Text>
          </View>
        ) : (
          dayGroups.map((group, index) => (
            <MealHistoryItem
              key={group.dateKey}
              group={group}
              isToday={index === 0 && isToday(parseISO(group.dateKey))}
              isLast={index === dayGroups.length - 1}
              onPress={() => handleDayPress(group.dateKey)}
            />
          ))
        )}
      </View>
    </View>
  );
};
